import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from '../../shared/Modal';
import AdminPaymentCardEntry from '../../shared/AdminPaymentCardEntry';
import {
  AdminSearchSite,
  AdminSearchSitesResponse,
  CustomerData,
  CreateReservationRequest,
  AdminBookingResponse,
  ProcessPaymentResponse,
  FeePreviewResponse,
  StorePaymentProvider,
  RecreationalVehicle,
} from '../../../types';
import { PaymentToken } from '../../../types/payment';
import managerApi from '../../../services/auth/managerApi';
import {
  getRecreationalVehicles,
  deleteRecreationalVehicle,
  updateMember,
  mapApiRecreationalVehicle,
  MemberUpdate,
} from '../../../services/memberService';
import RecreationalVehicleFormModal from '../../members/RecreationalVehicleFormModal';
import { getTodayString, addDays, parseBackendUtc } from '../../../utils/dateUtils';
import { formatPhoneInput } from '../../../utils/formatters';
import { FeeAdjustmentFields, DiscountCodeField, AppliedFeeLines } from '../shared/AdminFeeControls';
import {
  FeeAdjustmentState,
  EMPTY_FEE_ADJUSTMENT,
  isAdjustmentIncomplete,
  buildFeeAdjustmentPayload,
} from '../shared/feeAdjustmentUtils';
import './CreateReservationWizard.css';

interface AdminCreateReservationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Pre-fill from a schedule-grid click-to-create interaction: skips straight to the Sites step. */
  initialSelection?: { siteAlias: string; checkIn: string };
}

type Step = 'store_dates' | 'sites' | 'customer' | 'fee_review' | 'payment' | 'confirmation';

const STEPS: { key: Step; label: string }[] = [
  { key: 'store_dates', label: 'Store & Dates' },
  { key: 'sites', label: 'Sites' },
  { key: 'customer', label: 'Customer' },
  { key: 'fee_review', label: 'Fee Review' },
  { key: 'payment', label: 'Payment' },
  { key: 'confirmation', label: 'Confirmation' },
];

const UNAVAILABLE_REASON_LABELS: Record<string, string> = {
  booked: 'Booked',
  site_blackout: 'Site Blackout',
  store_blackout: 'Store Blackout',
  group_event_blocked: 'Group Event',
  occupancy_exceeded: 'Occupancy Exceeded',
};

interface SelectedSiteEntry {
  site: AdminSearchSite;
  adults: number;
  children: number;
  pets: number;
  vehicles: number;
}

const AdminCreateReservationWizard: React.FC<AdminCreateReservationWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialSelection,
}) => {
  // Navigation
  const [currentStep, setCurrentStep] = useState<Step>('store_dates');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin context
  const [adminLevel, setAdminLevel] = useState(0);
  const [userStoreId, setUserStoreId] = useState<number | null>(null);

  // Step 1: Store & Dates
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [selectedStoreCode, setSelectedStoreCode] = useState('');
  const [bookingType, setBookingType] = useState<'regular' | 'group-event'>('regular');
  const [checkIn, setCheckIn] = useState('');
  const [days, setDays] = useState(1);
  const [groupEventCode, setGroupEventCode] = useState('');
  const [groupEventInstance, setGroupEventInstance] = useState<any | null>(null);
  const [groupEventResults, setGroupEventResults] = useState<any[]>([]);
  const [groupEventSearching, setGroupEventSearching] = useState(false);
  const [showGroupEventDropdown, setShowGroupEventDropdown] = useState(false);
  const [storePaymentProviders, setStorePaymentProviders] = useState<StorePaymentProvider[]>([]);

  // Step 2: Sites
  const [searchSitesResult, setSearchSitesResult] = useState<AdminSearchSitesResponse | null>(null);
  const [selectedSites, setSelectedSites] = useState<SelectedSiteEntry[]>([]);
  const [overageLimits, setOverageLimits] = useState<Record<string, number | null>>({
    adult: null, child: null, pet: null, vehicle: null,
  });

  // Step 3: Customer
  const [memberEmail, setMemberEmail] = useState('');
  const [foundMember, setFoundMember] = useState<any | null>(null);
  const [memberLookupDone, setMemberLookupDone] = useState(false);
  const [memberLookupLoading, setMemberLookupLoading] = useState(false);
  const [memberResults, setMemberResults] = useState<any[]>([]);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [allMembers, setAllMembers] = useState<any[]>([]);
  const [createMemberAccount, setCreateMemberAccount] = useState(true);
  const [customerData, setCustomerData] = useState<CustomerData>({
    first_name: '', last_name: '', email: '',
    main_phone: '', second_phone: '',
    address: '', city: '', state: '',
    zip_code: '', country: 'United States',
  });
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRelation, setEmergencyRelation] = useState('');

  // Member profile & RV management (within wizard)
  const [memberRVs, setMemberRVs] = useState<RecreationalVehicle[]>([]);
  const [rvLoading, setRvLoading] = useState(false);
  const [showRVModal, setShowRVModal] = useState(false);
  const [editingRV, setEditingRV] = useState<RecreationalVehicle | null>(null);
  const [rvDeleteConfirm, setRvDeleteConfirm] = useState<number | null>(null);

  // Inline member update
  const [memberUpdateLoading, setMemberUpdateLoading] = useState(false);
  const [memberUpdateSuccess, setMemberUpdateSuccess] = useState(false);

  // Step 4: Fee Review
  const [feePreview, setFeePreview] = useState<FeePreviewResponse | null>(null);
  const [feePreviewLoading, setFeePreviewLoading] = useState(false);
  const [feePreviewError, setFeePreviewError] = useState<string | null>(null);
  // Discount code, fee adjustment, internal notes
  const [discountCode, setDiscountCode] = useState('');
  const [feeAdjustment, setFeeAdjustment] = useState<FeeAdjustmentState>(EMPTY_FEE_ADJUSTMENT);
  const [internalNotes, setInternalNotes] = useState('');

  // Step 5: Payment
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'check' | 'card_terminal' | 'card_moto' | 'payment_link'>('cash');
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending'>('paid');
  const [terminalTransactionId, setTerminalTransactionId] = useState('');
  const [cardToken, setCardToken] = useState<PaymentToken | null>(null);
  const [emailAddressConfirmed, setEmailAddressConfirmed] = useState(false);

  // Step 6: Confirmation
  const [bookingResult, setBookingResult] = useState<AdminBookingResponse | null>(null);
  const [retryPaymentMethod, setRetryPaymentMethod] = useState<string | null>(null);
  const [retryCardToken, setRetryCardToken] = useState<PaymentToken | null>(null);
  // Bumped whenever a retry consumes the card token, to clear the card entry's
  // "Card accepted" message along with it.
  const [retryCardResetSignal, setRetryCardResetSignal] = useState(0);
  const [retryTerminalTransactionId, setRetryTerminalTransactionId] = useState('');
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [linkFallbackLoading, setLinkFallbackLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);
  const modalBodyRef = useRef<HTMLDivElement | null>(null);

  // Scroll modal body to top on step change
  useEffect(() => {
    const modalBody = modalBodyRef.current?.closest('.modal-body');
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
  }, [currentStep]);

  // Reset email confirmation when email changes
  useEffect(() => {
    setEmailAddressConfirmed(false);
  }, [customerData.email]);

  // ---- Effects ----

  // Load admin context on open
  useEffect(() => {
    if (!isOpen) return;
    const managerData = JSON.parse(localStorage.getItem('managerUserData') || '{}');
    setAdminLevel(managerData.level || 0);
    setUserStoreId(managerData.store_id || null);

    if (!checkIn) setCheckIn(getTodayString());

    if (managerData.store_id) {
      setSelectedStoreId(managerData.store_id);
      setSelectedStoreCode(managerData.store_code || '');
    }

    loadStores(managerData.corporate_id, managerData.store_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Pre-fill from a schedule-grid click-to-create interaction: seed check-in/days from the clicked cell
  const appliedInitialSelectionKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      appliedInitialSelectionKey.current = null;
      return;
    }
    if (!initialSelection) return;
    const key = `${initialSelection.siteAlias}|${initialSelection.checkIn}`;
    if (appliedInitialSelectionKey.current === key) return;
    appliedInitialSelectionKey.current = key;
    setCheckIn(initialSelection.checkIn);
    setDays(1);
  }, [isOpen, initialSelection]);

  // Once the store/dates are resolved, skip the manual Store & Dates step and jump straight to Sites
  const autoAdvancedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || !initialSelection) return;
    if (currentStep !== 'store_dates') return;
    if (!selectedStoreCode || !checkIn || days < 1) return;
    const key = `${initialSelection.siteAlias}|${initialSelection.checkIn}`;
    if (autoAdvancedKey.current === key) return;
    autoAdvancedKey.current = key;
    (async () => {
      await searchSites();
      setCurrentStep('sites');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSelection, currentStep, selectedStoreCode, checkIn, days]);

  // Once search results arrive, auto-select the site the manager clicked on the grid
  useEffect(() => {
    if (!initialSelection || !searchSitesResult) return;
    if (selectedSites.length > 0) return;
    const match = searchSitesResult.sites.find(s => s.alias === initialSelection.siteAlias);
    if (!match) return;
    if (match.available === false || match.seasonal) {
      setError('The selected site is no longer available for these dates — choose another site below.');
      return;
    }
    toggleSite(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelection, searchSitesResult, selectedSites.length]);

  // Load payment providers and overage limits when store changes
  useEffect(() => {
    if (selectedStoreId) {
      loadPaymentProviders(selectedStoreId);
      // Load overage fee limits from store config
      managerApi.get(`/store-overage-fees/?store_id=${selectedStoreId}`).then((fees: any[]) => {
        const limits: Record<string, number | null> = { adult: null, child: null, pet: null, vehicle: null };
        for (const fee of fees || []) {
          if (fee.fee_type && fee.fee_type in limits) {
            const current = limits[fee.fee_type];
            // Use the highest max_allowed if multiple fee entries exist for the same type
            if (fee.max_allowed != null && (current == null || fee.max_allowed > current)) {
              limits[fee.fee_type] = fee.max_allowed;
            }
          }
        }
        setOverageLimits(limits);
      }).catch(() => {
        // If fetch fails, keep defaults (no limits)
        setOverageLimits({ adult: null, child: null, pet: null, vehicle: null });
      });
    }
  }, [selectedStoreId]);

  // Expiry countdown timer
  useEffect(() => {
    if (!bookingResult?.expires_at) return;
    const interval = setInterval(() => {
      const now = new Date();
      const expires = parseBackendUtc(bookingResult.expires_at!);
      const diff = expires.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeRemaining('EXPIRED');
        clearInterval(interval);
      } else {
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [bookingResult?.expires_at]);

  // Debounced group event search
  useEffect(() => {
    if (!groupEventCode || groupEventInstance) return;
    const timer = setTimeout(() => searchGroupEvents(groupEventCode), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupEventCode, selectedStoreId]);

  // Debounced member search
  useEffect(() => {
    if (!memberEmail || foundMember) return;
    const timer = setTimeout(() => searchMembers(memberEmail), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberEmail]);

  // ---- API Functions ----

  const loadStores = async (corporateId?: number, storeId?: number) => {
    try {
      const data = await managerApi.get('/stores/');
      let storeList = Array.isArray(data) ? data : data.stores || [];

      // Corporate admins see only their corporate's stores
      if (corporateId && !storeId) {
        storeList = storeList.filter((s: any) => s.corporate_id === corporateId);
      }
      // Super admins see all stores; store admins are locked (no dropdown)

      setStores(storeList);
    } catch (err: any) {
      console.error('Failed to load stores:', err);
    }
  };

  const loadPaymentProviders = async (storeId: number) => {
    try {
      const data = await managerApi.get(`/store-payment-providers?store_id=${storeId}&active_only=true`);
      const providers: StorePaymentProvider[] = data.configurations || [];
      providers.sort((a, b) => b.priority - a.priority);
      setStorePaymentProviders(providers);
    } catch (err) {
      console.error('Failed to load payment providers:', err);
    }
  };

  const searchGroupEvents = useCallback(async (query: string) => {
    if (!query || query.length < 2 || !selectedStoreId) {
      setGroupEventResults([]);
      setShowGroupEventDropdown(false);
      return;
    }
    setGroupEventSearching(true);
    try {
      const data = await managerApi.get(
        `/groupevent-bookings/instances?store_id=${selectedStoreId}`
      );
      const instances = Array.isArray(data) ? data : data.instances || [];
      const q = query.toLowerCase();
      const filtered = instances.filter((inst: any) => {
        const code = (inst.booking_code || '').toLowerCase();
        const name = (inst.name || inst.event?.name || '').toLowerCase();
        return code.includes(q) || name.includes(q);
      });
      setGroupEventResults(filtered);
      setShowGroupEventDropdown(filtered.length > 0);
    } catch (err) {
      setGroupEventResults([]);
      setShowGroupEventDropdown(false);
    } finally {
      setGroupEventSearching(false);
    }
  }, [selectedStoreId]);

  const selectGroupEvent = (instance: any) => {
    setGroupEventInstance(instance);
    setGroupEventCode(instance.booking_code || '');
    setShowGroupEventDropdown(false);
    setGroupEventResults([]);
  };

  const searchSites = useCallback(async () => {
    if (!selectedStoreCode || !checkIn || days < 1) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = bookingType === 'group-event'
        ? '/groupevent-bookings/search-sites'
        : '/reservations/search-sites';

      const payload: any = { store_code: selectedStoreCode, check_in: checkIn, days };
      if (bookingType === 'group-event') {
        payload.booking_code = groupEventCode;
      }

      const data: AdminSearchSitesResponse = await managerApi.post(endpoint, payload);
      setSearchSitesResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to search sites');
    } finally {
      setLoading(false);
    }
  }, [selectedStoreCode, checkIn, days, bookingType, groupEventCode]);

  const loadMembersOnce = useCallback(async () => {
    if (allMembers.length > 0) return allMembers;
    try {
      const members = await managerApi.get('/members');
      const memberList = Array.isArray(members) ? members : members.members || [];
      setAllMembers(memberList);
      return memberList;
    } catch (err) {
      console.error('Failed to load members:', err);
      return [];
    }
  }, [allMembers]);

  const searchMembers = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setMemberResults([]);
      setShowMemberDropdown(false);
      return;
    }
    setMemberLookupLoading(true);
    try {
      const memberList = await loadMembersOnce();
      const q = query.toLowerCase();
      const filtered = memberList.filter((m: any) => {
        const email = (m.email || '').toLowerCase();
        const firstName = (m.first_name || m.firstName || '').toLowerCase();
        const lastName = (m.last_name || m.lastName || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`;
        return email.includes(q) || firstName.includes(q) || lastName.includes(q) || fullName.includes(q);
      }).slice(0, 10);
      setMemberResults(filtered);
      setShowMemberDropdown(filtered.length > 0);
    } catch (err) {
      setMemberResults([]);
      setShowMemberDropdown(false);
    } finally {
      setMemberLookupLoading(false);
    }
  }, [loadMembersOnce]);

  const loadMemberRVs = useCallback(async (memberId: number) => {
    setRvLoading(true);
    try {
      const apiRVs = await getRecreationalVehicles(memberId);
      setMemberRVs(apiRVs.map(mapApiRecreationalVehicle));
    } catch (err) {
      console.error('Failed to load member RVs:', err);
      setMemberRVs([]);
    } finally {
      setRvLoading(false);
    }
  }, []);

  const handleInlineUpdateMember = async () => {
    if (!foundMember?.id) return;
    setMemberUpdateLoading(true);
    setMemberUpdateSuccess(false);
    setError(null);
    try {
      const data: MemberUpdate = {
        first_name: customerData.first_name,
        last_name: customerData.last_name,
        email: customerData.email,
        main_phone: customerData.main_phone || undefined,
        second_phone: customerData.second_phone || undefined,
        address: customerData.address || undefined,
        city: customerData.city || undefined,
        state: customerData.state || undefined,
        zip_code: customerData.zip_code || undefined,
        country: customerData.country || undefined,
        emergency_name: emergencyName || undefined,
        emergency_phone: emergencyPhone || undefined,
        emergency_relation: emergencyRelation || undefined,
      };
      const updatedApi = await updateMember(foundMember.id, data);
      setFoundMember(updatedApi);
      // Refresh the cached member list so re-searching shows updated data
      setAllMembers(prev => prev.map(m => m.id === foundMember.id ? updatedApi : m));
      setMemberUpdateSuccess(true);
    } catch (err: any) {
      console.error('Failed to update member:', err);
      setError(err.message || 'Failed to update member profile');
    } finally {
      setMemberUpdateLoading(false);
    }
  };

  const handleDeleteRV = async (vehicleId: number) => {
    try {
      await deleteRecreationalVehicle(vehicleId);
      setRvDeleteConfirm(null);
      if (foundMember?.id) loadMemberRVs(foundMember.id);
    } catch (err: any) {
      console.error('Failed to delete RV:', err);
      setError(err.message || 'Failed to delete vehicle');
    }
  };

  const selectMember = (match: any) => {
    setFoundMember(match);
    setMemberEmail(match.email || '');
    setCustomerData({
      first_name: match.first_name || match.firstName || '',
      last_name: match.last_name || match.lastName || '',
      email: match.email || '',
      main_phone: match.phone || match.main_phone || '',
      second_phone: match.second_phone || match.secondPhone || '',
      address: match.address || '',
      city: match.city || '',
      state: match.state || '',
      zip_code: match.zip_code || match.zipCode || '',
      country: match.country || 'United States',
    });
    setEmergencyName(match.emergency_name || '');
    setEmergencyPhone(match.emergency_phone || '');
    setEmergencyRelation(match.emergency_relation || '');
    setCreateMemberAccount(false);
    setMemberLookupDone(true);
    setShowMemberDropdown(false);
    setMemberResults([]);
    if (match.id) loadMemberRVs(match.id);
  };

  const confirmNoMember = () => {
    setFoundMember(null);
    setCustomerData({
      first_name: '', last_name: '', email: memberEmail,
      main_phone: '', second_phone: '',
      address: '', city: '', state: '',
      zip_code: '', country: 'United States',
    });
    setEmergencyName('');
    setEmergencyPhone('');
    setEmergencyRelation('');
    setMemberRVs([]);
    setCreateMemberAccount(true);
    setMemberLookupDone(true);
    setShowMemberDropdown(false);
    setMemberResults([]);
  };

  const loadFeePreview = useCallback(async () => {
    setFeePreviewLoading(true);
    setFeePreviewError(null);
    try {
      const payload: any = {
        store_id: selectedStoreId,
        check_in: checkIn,
        days,
        sites: selectedSites.map(s => ({
          site_alias: s.site.alias,
          adults: s.adults,
          children: s.children,
          pets: s.pets,
          vehicles: s.vehicles,
        })),
      };
      if (bookingType === 'group-event' && groupEventCode) {
        payload.booking_code = groupEventCode;
      }
      if (discountCode.trim()) {
        payload.discount_code = discountCode.trim();
      }
      // Only send the adjustment once it has a reason (backend 422s without it)
      if (!isAdjustmentIncomplete(feeAdjustment)) {
        Object.assign(payload, buildFeeAdjustmentPayload(feeAdjustment));
      }
      const data: FeePreviewResponse = await managerApi.post('/reservations/preview-admin-booking', payload);
      setFeePreview(data);
    } catch (err: any) {
      setFeePreviewError(err.message || 'Failed to load fee preview');
    } finally {
      setFeePreviewLoading(false);
    }
  }, [selectedStoreId, checkIn, days, selectedSites, bookingType, groupEventCode, discountCode, feeAdjustment]);

  // Builds the create-for-customer payload from the wizard's collected data.
  // Takes the payment method as an argument rather than reading `paymentMethod`,
  // so the payment-link fallback on the failure screen can re-submit the same
  // booking under a different method without the manager re-keying any of it.
  const buildCreateRequest = (method: string): CreateReservationRequest => {
    const request: CreateReservationRequest = {
      store_id: selectedStoreId!,
      check_in: checkIn,
      days,
      sites: selectedSites.map(s => ({
        site_alias: s.site.alias,
        adults: s.adults, children: s.children, pets: s.pets, vehicles: s.vehicles,
      })),
      payment_method: method,
      // A payment link is by definition unpaid at creation, whatever the
      // manager picked before the card failed.
      payment_status: method === 'payment_link' ? 'pending' : paymentStatus,
      special_requests: JSON.stringify({ payment_type: method }),
      internal_notes: internalNotes || undefined,
      booking_code: bookingType === 'group-event' ? groupEventCode : undefined,
      emergency_name: emergencyName || undefined,
      emergency_phone: emergencyPhone || undefined,
      emergency_relation: emergencyRelation || undefined,
      ...(discountCode.trim() ? { discount_code: discountCode.trim() } : {}),
      ...buildFeeAdjustmentPayload(feeAdjustment),
    };

    // Only include optional fields when they have values
    // (backend rejects empty strings for phone/zip validators)
    const cleanedCustomerData: any = {
      first_name: customerData.first_name,
      last_name: customerData.last_name,
      email: customerData.email,
    };
    if (customerData.main_phone) cleanedCustomerData.main_phone = customerData.main_phone;
    if (customerData.second_phone) cleanedCustomerData.second_phone = customerData.second_phone;
    if (customerData.address) cleanedCustomerData.address = customerData.address;
    if (customerData.city) cleanedCustomerData.city = customerData.city;
    if (customerData.state) cleanedCustomerData.state = customerData.state;
    if (customerData.zip_code) cleanedCustomerData.zip_code = customerData.zip_code;
    if (customerData.country) cleanedCustomerData.country = customerData.country;

    if (foundMember) {
      request.member_id = foundMember.id;
      request.customer_data = cleanedCustomerData;
      request.create_member_account = false;
    } else {
      request.customer_data = cleanedCustomerData;
      request.create_member_account = createMemberAccount;
      if (createMemberAccount) {
        request.send_welcome_email = true;
      }
    }

    if (method === 'payment_link') {
      request.send_payment_link = true;
    }
    if (method === 'card_moto' && cardToken) {
      request.card_token = cardToken.token;
      request.card_last_four = cardToken.last_four_digits;
      request.card_brand = cardToken.card_brand;
      request.gateway_provider = cardToken.gateway_provider;
    }
    if (method === 'card_terminal') {
      request.terminal_transaction_id = terminalTransactionId;
    }

    return request;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const request = buildCreateRequest(paymentMethod);

      const data: AdminBookingResponse = await managerApi.post('/reservations/create-for-customer', request);
      setBookingResult(data);
      setCurrentStep('confirmation');

      if (data.success && data.status === 'confirmed' && onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setBookingResult({ success: false, error: err.message || 'Failed to create reservation' });
      setCurrentStep('confirmation');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryPayment = async (method: string, retryToken?: PaymentToken) => {
    if (!bookingResult?.reservation_id) return;
    setRetryLoading(true);
    setRetryError(null);
    let charged = false;
    try {
      let payload: any = {};
      if (method === 'cash' || method === 'check') {
        payload = { payment_status: 'paid' };
      } else if (method === 'card_terminal') {
        payload = {
          payment_status: 'paid',
          terminal_transaction_id: retryTerminalTransactionId.trim(),
        };
      } else if (method === 'card_moto' && retryToken) {
        payload = {
          card_token: retryToken.token,
          card_last_four: retryToken.last_four_digits,
          card_brand: retryToken.card_brand,
          gateway_provider: retryToken.gateway_provider,
        };
      }

      const data: ProcessPaymentResponse = await managerApi.post(
        `/reservations/${bookingResult.reservation_id}/process-payment/${method}`,
        payload
      );

      if (data.success) {
        charged = true;
        setBookingResult(prev => prev ? {
          ...prev,
          status: 'confirmed',
          payment_status: 'paid',
          payment_id: data.payment_id,
          payment_error: undefined,
        } : prev);
        if (onSuccess) onSuccess();
      } else {
        // The reservation stays pending_payment on a decline, so another card —
        // or the same one, re-keyed — can still be charged against it. Say so:
        // the decline reason alone leaves the manager guessing.
        const reason = data.payment_error || 'Payment retry failed';
        setRetryError(
          method === 'card_moto'
            ? `${reason} — re-enter the card details and click "Validate Card" to try again.`
            : reason
        );
      }
    } catch (err: any) {
      // The backend holds a MOTO reservation for 10 minutes, then a cleanup job
      // archives it (active=false). Past that point process-payment answers with
      // "has expired" (410) or, once archived, "not found or not in
      // pending_payment status" (404) — neither of which tells the manager what
      // to do next. Treat both as an expired hold: the countdown may still show
      // time left if this client's clock lags the server's.
      const detail: string = err?.message || '';
      const holdGone =
        detail.includes('has expired') ||
        detail.includes('not in pending_payment status');
      if (holdGone) {
        // The expired banner below explains it; a second message would just
        // repeat it.
        setTimeRemaining('EXPIRED');
        setRetryError(null);
      } else {
        setRetryError(detail || 'Payment retry failed');
      }
    } finally {
      setRetryLoading(false);
      // A gateway token is single-use — the attempt above spent it whether or
      // not the charge landed. Drop it so the "Charge Card" button goes away
      // and the manager is returned to "Validate Card", instead of re-sending a
      // token the gateway will reject on sight.
      if (method === 'card_moto' && !charged) {
        setRetryCardToken(null);
        setRetryCardResetSignal(n => n + 1);
      }
    }
  };

  /**
   * Fall back to emailing the customer a payment link after their card failed.
   *
   * There is no backend route that attaches a payment link to an existing
   * reservation — process-payment rejects `payment_link`, and resend/renew both
   * need a token this reservation never had — so this books again from the same
   * wizard data under the payment-link method. The manager re-keys nothing, but
   * it is a NEW reservation with a new confirmation code; the failed one is left
   * to lapse and be archived.
   *
   * Only offered once the failed reservation's hold has expired: until then it
   * still holds the site, and create-for-customer would refuse with a 409
   * against the manager's own hold.
   */
  const handleSendPaymentLinkInstead = async () => {
    setLinkFallbackLoading(true);
    setRetryError(null);
    try {
      const data: AdminBookingResponse = await managerApi.post(
        '/reservations/create-for-customer',
        buildCreateRequest('payment_link')
      );
      setBookingResult(data);
      if (data.success && onSuccess) onSuccess();
    } catch (err: any) {
      setRetryError(err?.message || 'Failed to send the payment link');
    } finally {
      setLinkFallbackLoading(false);
    }
  };

  // ---- Navigation ----

  const handleNext = async () => {
    setError(null);
    const nextIndex = currentStepIndex + 1;
    if (nextIndex >= STEPS.length) return;

    const nextStep = STEPS[nextIndex].key;

    if (nextStep === 'sites') {
      await searchSites();
    } else if (nextStep === 'fee_review') {
      await loadFeePreview();
    } else if (nextStep === 'confirmation') {
      await handleSubmit();
      return; // handleSubmit sets the step
    }

    setCurrentStep(nextStep);
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setError(null);
      setCurrentStep(STEPS[prevIndex].key);
    }
  };

  const resetWizard = () => {
    setCurrentStep('store_dates');
    setLoading(false);
    setError(null);
    // Preserve store context (stores, selectedStoreId, selectedStoreCode, storePaymentProviders,
    // overageLimits) — the isOpen effect won't re-fire since the modal stays open, so these
    // values must remain to avoid a missing campground ID on the next booking.
    setBookingType('regular');
    setCheckIn(getTodayString()); // set directly since the isOpen effect won't re-fire
    setDays(1);
    setGroupEventCode('');
    setGroupEventInstance(null);
    setGroupEventResults([]);
    setGroupEventSearching(false);
    setShowGroupEventDropdown(false);
    setSearchSitesResult(null);
    setSelectedSites([]);
    setMemberEmail('');
    setFoundMember(null);
    setMemberRVs([]);
    setShowRVModal(false);
    setEditingRV(null);
    setRvDeleteConfirm(null);
    setMemberUpdateLoading(false);
    setMemberUpdateSuccess(false);
    setMemberLookupDone(false);
    setMemberResults([]);
    setShowMemberDropdown(false);
    setAllMembers([]);
    setCreateMemberAccount(true);
    setCustomerData({
      first_name: '', last_name: '', email: '',
      main_phone: '', second_phone: '',
      address: '', city: '', state: '',
      zip_code: '', country: 'United States',
    });
    setFeePreview(null);
    setFeePreviewError(null);
    setPaymentMethod('cash');
    setPaymentStatus('paid');
    setTerminalTransactionId('');
    setCardToken(null);
    setEmailAddressConfirmed(false);
    setBookingResult(null);
    setRetryPaymentMethod(null);
    setRetryCardToken(null);
    setRetryTerminalTransactionId('');
    setRetryError(null);
    setTimeRemaining('');
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'store_dates':
        return !!(selectedStoreId && checkIn && days >= 1 &&
          (bookingType === 'regular' || groupEventInstance));
      case 'sites':
        return selectedSites.length > 0 && selectedSites.every(s => s.adults >= 1);
      case 'customer':
        return !!(memberLookupDone && customerData.first_name.trim() && customerData.last_name.trim()
          && customerData.email.trim() && Object.keys(validationErrors).length === 0);
      case 'fee_review':
        return !!(feePreview && !feePreviewError);
      case 'payment':
        if (!emailAddressConfirmed) return false;
        if (paymentMethod === 'card_terminal') return !!terminalTransactionId.trim();
        if (paymentMethod === 'card_moto') return !!cardToken;
        if (paymentMethod === 'payment_link') return !!customerData.email.trim();
        return true;
      default:
        return true;
    }
  };

  // ---- Site toggle ----

  const toggleSite = (site: AdminSearchSite) => {
    if (site.available === false || site.seasonal) return;
    setSelectedSites(prev => {
      const existing = prev.find(s => s.site.alias === site.alias);
      if (existing) {
        return prev.filter(s => s.site.alias !== site.alias);
      }
      return [...prev, { site, adults: 1, children: 0, pets: 0, vehicles: 0 }];
    });
  };

  const updateSiteOccupancy = (alias: string, field: keyof SelectedSiteEntry, value: number) => {
    setSelectedSites(prev => prev.map(s =>
      s.site.alias === alias ? { ...s, [field]: value } : s
    ));
  };

  // ---- Step Indicator ----

  const renderStepIndicator = () => (
    <div className="wizard-steps">
      {STEPS.slice(0, -1).map((step, index) => (
        <div
          key={step.key}
          className={`wizard-step ${index <= currentStepIndex ? 'active' : ''} ${
            index < currentStepIndex ? 'completed' : ''
          }`}
        >
          <span className="step-dot">{index < currentStepIndex ? '\u2713' : index + 1}</span>
          <span className="step-label">{step.label}</span>
        </div>
      ))}
    </div>
  );

  // ---- Step 1: Store & Dates ----

  const isStoreLocked = !!userStoreId;

  const handleStoreChange = (storeId: number) => {
    const store = stores.find((s: any) => s.id === storeId);
    setSelectedStoreId(storeId);
    setSelectedStoreCode(store?.store_code || '');
  };

  const renderStoreDatesStep = () => (
    <div className="wizard-content">
      <h3 className="wizard-section-title">Store & Dates</h3>

      {/* Store Selection */}
      <div className="form-group">
        <label>Store</label>
        {isStoreLocked ? (
          <div className="store-locked">
            <span className="store-name">
              {stores.find((s: any) => s.id === selectedStoreId)?.name || selectedStoreCode || `Store #${selectedStoreId}`}
            </span>
          </div>
        ) : (
          <select
            className="form-input"
            value={selectedStoreId || ''}
            onChange={(e) => handleStoreChange(Number(e.target.value))}
          >
            <option value="">Select a store...</option>
            {stores.map((store: any) => (
              <option key={store.id} value={store.id}>
                {store.name || store.store_code}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Booking Type */}
      <div className="form-group">
        <label>Booking Type</label>
        <div className="booking-type-toggle">
          <label className="radio-option">
            <input
              type="radio"
              name="bookingType"
              checked={bookingType === 'regular'}
              onChange={() => { setBookingType('regular'); setGroupEventInstance(null); }}
            />
            <span>Regular</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="bookingType"
              checked={bookingType === 'group-event'}
              onChange={() => setBookingType('group-event')}
            />
            <span>Group Event</span>
          </label>
        </div>
      </div>

      {/* Group Event Lookup */}
      {bookingType === 'group-event' && (
        <div className="group-event-section">
          <div className="form-group" style={{ position: 'relative' }}>
            <label>Search Booking Code or Event Name</label>
            <input
              type="text"
              className="form-input"
              value={groupEventCode}
              onChange={(e) => {
                setGroupEventCode(e.target.value);
                if (groupEventInstance) {
                  setGroupEventInstance(null);
                }
              }}
              placeholder="Type to search (e.g. EVT2026 or Rally)"
            />
            {groupEventSearching && (
              <div className="lookup-hint">Searching...</div>
            )}
            {showGroupEventDropdown && groupEventResults.length > 0 && (
              <div className="lookup-dropdown">
                {groupEventResults.map((inst, i) => (
                  <div
                    key={inst.id || i}
                    className="lookup-dropdown-item"
                    onClick={() => selectGroupEvent(inst)}
                  >
                    <strong>{inst.booking_code}</strong>
                    <span> — {inst.name || inst.event?.name}</span>
                    {inst.start_date && (
                      <span className="lookup-dropdown-meta"> ({inst.start_date} - {inst.end_date})</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {groupEventInstance && (
            <div className="group-event-info">
              Event: <strong>{groupEventInstance.name || groupEventInstance.event?.name}</strong>
              {groupEventInstance.start_date && ` (${groupEventInstance.start_date} - ${groupEventInstance.end_date})`}
            </div>
          )}
        </div>
      )}

      {/* Dates */}
      <div className="form-grid">
        <div className="form-group">
          <label>Check-in Date</label>
          <input
            type="date"
            className="form-input"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            min={getTodayString()}
          />
        </div>
        <div className="form-group">
          <label>Number of Nights</label>
          <input
            type="number"
            className="form-input"
            min="1"
            max="30"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value) || 1)}
          />
        </div>
      </div>

      {checkIn && days > 0 && (
        <div className="date-summary">
          <p><strong>Check-in:</strong> {checkIn}</p>
          <p><strong>Check-out:</strong> {addDays(checkIn, days)}</p>
          <p><strong>Duration:</strong> {days} night{days > 1 ? 's' : ''}</p>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  // ---- Step 2: Sites ----

  const renderSitesStep = () => {
    const sites = [...(searchSitesResult?.sites || [])].sort((a, b) => {
      const sectionCmp = (a.section_name || '').localeCompare(b.section_name || '', undefined, { numeric: true, sensitivity: 'base' });
      if (sectionCmp !== 0) return sectionCmp;
      return a.alias.localeCompare(b.alias, undefined, { numeric: true, sensitivity: 'base' });
    });
    const availableCount = sites.filter(s => s.available !== false && !s.seasonal).length;
    const totalCount = sites.length;

    // Group sites by section
    const sectionOrder: string[] = [];
    const sitesBySection: Record<string, typeof sites> = {};
    sites.forEach(site => {
      const section = site.section_name || 'Other';
      if (!sitesBySection[section]) {
        sitesBySection[section] = [];
        sectionOrder.push(section);
      }
      sitesBySection[section].push(site);
    });

    return (
      <div className="wizard-content">
        <h3 className="wizard-section-title">Select Sites</h3>

        {loading && <div className="loading">Loading sites...</div>}

        {!loading && sites.length > 0 && (
          <div className="site-count-summary">
            {availableCount} of {totalCount} sites available
          </div>
        )}

        {sectionOrder.map(section => (
          <div key={section}>
            <h4 className="wizard-section-title" style={{ fontSize: '0.9rem', margin: '1.25rem 0 0.5rem', color: 'var(--text-secondary, #666)' }}>
              {section}
            </h4>
            <div className="site-grid">
          {sitesBySection[section].map((site) => {
            const isUnavailable = site.available === false || site.seasonal;
            const isSelected = selectedSites.some(s => s.site.alias === site.alias);
            const selectedEntry = selectedSites.find(s => s.site.alias === site.alias);
            const reasonLabel = site.seasonal ? 'Seasonal' :
              (site.unavailable_reason ? UNAVAILABLE_REASON_LABELS[site.unavailable_reason] || site.unavailable_reason : '');

            return (
              <div
                key={site.alias}
                className={`site-card ${isUnavailable ? 'unavailable' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => !isUnavailable && toggleSite(site)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={isUnavailable}
                  onChange={() => {}}
                  style={{ marginTop: '2px' }}
                />
                <div style={{ flex: 1 }}>
                  <div className="site-card-header">
                    <strong>{site.alias}</strong>
                    {site.site_type && <span className="site-card-details">{site.site_type}</span>}
                    {site.price_per_night != null && (
                      <span className="site-card-price">${site.price_per_night}/night</span>
                    )}
                    {isUnavailable && reasonLabel && (
                      <span className="unavailable-reason">{reasonLabel}</span>
                    )}
                  </div>
                  {site.description && (
                    <div className="site-card-details">{site.description}{site.section_name ? ` \u2022 ${site.section_name}` : ''}</div>
                  )}

                  {/* Per-site occupancy */}
                  {isSelected && selectedEntry && (
                    <div className="site-occupancy-row" onClick={(e) => e.stopPropagation()}>
                      <div className="form-group">
                        <label>Adults{overageLimits.adult != null ? ` (max ${overageLimits.adult})` : ''}</label>
                        <input type="number" min="1" max={overageLimits.adult ?? 10} value={selectedEntry.adults}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 1;
                            const max = overageLimits.adult ?? 10;
                            updateSiteOccupancy(site.alias, 'adults', Math.min(val, max));
                          }} />
                      </div>
                      <div className="form-group">
                        <label>Children{overageLimits.child != null ? ` (max ${overageLimits.child})` : ''}</label>
                        <input type="number" min="0" max={overageLimits.child ?? 10} value={selectedEntry.children}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const max = overageLimits.child ?? 10;
                            updateSiteOccupancy(site.alias, 'children', Math.min(val, max));
                          }} />
                      </div>
                      <div className="form-group">
                        <label>Pets{overageLimits.pet != null ? ` (max ${overageLimits.pet})` : ''}</label>
                        <input type="number" min="0" max={overageLimits.pet ?? 10} value={selectedEntry.pets}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const max = overageLimits.pet ?? 10;
                            updateSiteOccupancy(site.alias, 'pets', Math.min(val, max));
                          }} />
                      </div>
                      <div className="form-group">
                        <label>Vehicles{overageLimits.vehicle != null ? ` (max ${overageLimits.vehicle})` : ''}</label>
                        <input type="number" min="0" max={overageLimits.vehicle ?? 10} value={selectedEntry.vehicles}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            const max = overageLimits.vehicle ?? 10;
                            updateSiteOccupancy(site.alias, 'vehicles', Math.min(val, max));
                          }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
            </div>
          </div>
        ))}

        {error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  // ---- Step 3: Customer ----

  const updateCustomerField = (field: keyof CustomerData, value: string) => {
    setCustomerData(prev => ({ ...prev, [field]: value }));
    setMemberUpdateSuccess(false);
  };

  // ---- Validation helpers (matching backend validators.py) ----

  const isValidEmail = (email: string): boolean => {
    if (!email.trim()) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const isValidPhone = (phone: string, country?: string): boolean => {
    if (!phone) return true; // optional field
    const digits = phone.replace(/\D/g, '');
    if (!digits) return true; // empty after strip = not provided
    const c = (country || 'United States').toUpperCase();
    const isNorthAmerica = ['US', 'USA', 'UNITED STATES', 'CA', 'CAN', 'CANADA'].includes(c);
    if (isNorthAmerica) {
      return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
    }
    return digits.length >= 7 && digits.length <= 15;
  };

  const isValidZip = (zip: string, country?: string): boolean => {
    if (!zip) return true; // optional field
    const trimmed = zip.trim();
    if (!trimmed) return true;
    const c = (country || 'United States').toUpperCase();
    if (['US', 'USA', 'UNITED STATES'].includes(c)) {
      return /^\d{5}(-\d{4})?$/.test(trimmed);
    }
    if (['CA', 'CAN', 'CANADA'].includes(c)) {
      return /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ]\s?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i.test(trimmed);
    }
    return trimmed.length >= 3 && trimmed.length <= 10;
  };

  const getValidationErrors = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!memberLookupDone) return errors;
    const d = customerData;
    if (!d.first_name.trim()) errors.first_name = 'Required';
    if (!d.last_name.trim()) errors.last_name = 'Required';
    if (!d.email.trim()) errors.email = 'Required';
    else if (!isValidEmail(d.email)) errors.email = 'Invalid email format';
    if (d.main_phone && !isValidPhone(d.main_phone, d.country)) errors.main_phone = 'Invalid phone (10 digits for US/CA)';
    if (d.second_phone && !isValidPhone(d.second_phone, d.country)) errors.second_phone = 'Invalid phone (10 digits for US/CA)';
    if (d.zip_code && !isValidZip(d.zip_code, d.country)) {
      const c = (d.country || 'United States').toUpperCase();
      if (['US', 'USA', 'UNITED STATES'].includes(c)) errors.zip_code = 'Invalid ZIP (e.g. 12345 or 12345-6789)';
      else if (['CA', 'CAN', 'CANADA'].includes(c)) errors.zip_code = 'Invalid postal code (e.g. A1A 1A1)';
      else errors.zip_code = 'Invalid postal code (3-10 characters)';
    }
    return errors;
  };

  const validationErrors = getValidationErrors();

  const hasMemberChanges = (): boolean => {
    if (!foundMember) return false;
    const m = foundMember;
    return (
      customerData.first_name !== (m.first_name || m.firstName || '') ||
      customerData.last_name !== (m.last_name || m.lastName || '') ||
      customerData.email !== (m.email || '') ||
      (customerData.main_phone || '') !== (m.phone || m.main_phone || '') ||
      (customerData.second_phone || '') !== (m.second_phone || m.secondPhone || '') ||
      (customerData.address || '') !== (m.address || '') ||
      (customerData.city || '') !== (m.city || '') ||
      (customerData.state || '') !== (m.state || '') ||
      (customerData.zip_code || '') !== (m.zip_code || m.zipCode || '') ||
      (customerData.country || 'United States') !== (m.country || 'United States') ||
      (emergencyName || '') !== (m.emergency_name || '') ||
      (emergencyPhone || '') !== (m.emergency_phone || '') ||
      (emergencyRelation || '') !== (m.emergency_relation || '')
    );
  };

  const renderCustomerStep = () => (
    <div className="wizard-content">
      <h3 className="wizard-section-title">Step 1: Search for Existing Member</h3>
      <p style={{ color: 'var(--text-secondary, #888)', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
        Search to pre-fill information. A new customer record is created for each booking.
      </p>

      {/* Email / Name Lookup */}
      <div className="form-group" style={{ position: 'relative' }}>
        <label>Search by Email or Name</label>
        <input
          type="text"
          className="form-input"
          value={memberEmail}
          onChange={(e) => {
            setMemberEmail(e.target.value);
            if (foundMember || memberLookupDone) {
              setFoundMember(null);
              setMemberLookupDone(false);
              setMemberUpdateSuccess(false);
            }
          }}
          placeholder="Type email or name to search..."
        />
        {memberLookupLoading && (
          <div className="lookup-hint">Searching...</div>
        )}
        {showMemberDropdown && memberResults.length > 0 && (
          <div className="lookup-dropdown">
            {memberResults.map((m, i) => (
              <div
                key={m.id || i}
                className="lookup-dropdown-item"
                onClick={() => selectMember(m)}
              >
                <strong>{m.first_name || m.firstName} {m.last_name || m.lastName}</strong>
                <span className="lookup-dropdown-meta"> — {m.email}</span>
                {m.phone && <span className="lookup-dropdown-meta"> | {m.phone}</span>}
              </div>
            ))}
          </div>
        )}
        {!showMemberDropdown && memberEmail.length >= 2 && !foundMember && !memberLookupLoading && !memberLookupDone && (
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 'var(--spacing-sm)' }}
            onClick={confirmNoMember}
          >
            Member Not Found — Create a New Customer for this Reservation
          </button>
        )}
      </div>

      {memberLookupDone && (
        <div className={`member-lookup-result ${foundMember ? 'found' : 'not-found'}`}>
          {foundMember
            ? `Member found: ${foundMember.first_name || foundMember.firstName} ${foundMember.last_name || foundMember.lastName} (#${foundMember.id})`
            : 'No member found — enter customer information below'}
        </div>
      )}

      {foundMember && memberLookupDone && hasMemberChanges() && !memberUpdateSuccess && (
        <div style={{
          background: 'var(--warning-bg, #fff3cd)', border: '1px solid var(--warning-border, #ffc107)',
          borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--warning-text, #856404)' }}>
              Member information has been modified. These changes will be used for this booking.
            </span>
            <button type="button" className="btn btn-primary"
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.6rem', whiteSpace: 'nowrap' }}
              onClick={handleInlineUpdateMember} disabled={memberUpdateLoading}>
              {memberUpdateLoading ? 'Updating...' : 'Update Member Profile'}
            </button>
          </div>
        </div>
      )}
      {foundMember && memberLookupDone && memberUpdateSuccess && (
        <div style={{
          background: 'var(--success-bg, #d4edda)', border: '1px solid var(--success-border, #28a745)',
          borderRadius: '6px', padding: '0.5rem 1rem', marginBottom: '1rem',
          color: 'var(--success-text, #155724)', fontSize: '0.85rem',
        }}>
          Member profile updated!
        </div>
      )}

      {/* Step 2 heading with badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
        <h4 className="wizard-section-title" style={{ fontSize: '0.95rem', margin: 0 }}>
          Step 2: Customer Information for This Booking
        </h4>
        {memberLookupDone && foundMember && (
          <span style={{ background: 'var(--primary, #007bff)', color: '#fff', fontSize: '0.7rem',
            padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 600 }}>MEMBER</span>
        )}
        {memberLookupDone && !foundMember && (
          <span style={{ background: 'var(--warning, #e08a00)', color: '#fff', fontSize: '0.7rem',
            padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 600 }}>NEW</span>
        )}
      </div>

      {/* Customer Fields */}
      <div className="form-grid">
        <div className="form-group">
          <label>First Name <span className="required">*</span></label>
          <input type="text" className={`form-input${validationErrors.first_name ? ' input-error' : ''}`}
            value={customerData.first_name}
            onChange={(e) => updateCustomerField('first_name', e.target.value)} placeholder="John"
            disabled={!memberLookupDone} maxLength={50} />
          {validationErrors.first_name && <span className="field-error">{validationErrors.first_name}</span>}
        </div>
        <div className="form-group">
          <label>Last Name <span className="required">*</span></label>
          <input type="text" className={`form-input${validationErrors.last_name ? ' input-error' : ''}`}
            value={customerData.last_name}
            onChange={(e) => updateCustomerField('last_name', e.target.value)} placeholder="Doe"
            disabled={!memberLookupDone} maxLength={50} />
          {validationErrors.last_name && <span className="field-error">{validationErrors.last_name}</span>}
        </div>
        <div className="form-group">
          <label>Email <span className="required">*</span></label>
          <input type="email" className={`form-input${validationErrors.email ? ' input-error' : ''}`}
            value={customerData.email}
            onChange={(e) => updateCustomerField('email', e.target.value)}
            disabled={!memberLookupDone || !!foundMember} placeholder="john@example.com" maxLength={100} />
          {validationErrors.email && <span className="field-error">{validationErrors.email}</span>}
        </div>
        <div className="form-group">
          <label>Main Phone</label>
          <input type="tel" className={`form-input${validationErrors.main_phone ? ' input-error' : ''}`}
            value={customerData.main_phone || ''}
            onChange={(e) => updateCustomerField('main_phone', formatPhoneInput(e.target.value))} placeholder="(555) 123-4567"
            disabled={!memberLookupDone} maxLength={20} />
          {validationErrors.main_phone && <span className="field-error">{validationErrors.main_phone}</span>}
        </div>
        <div className="form-group">
          <label>Second Phone</label>
          <input type="tel" className={`form-input${validationErrors.second_phone ? ' input-error' : ''}`}
            value={customerData.second_phone || ''}
            onChange={(e) => updateCustomerField('second_phone', formatPhoneInput(e.target.value))} placeholder="(555) 123-4567"
            disabled={!memberLookupDone} maxLength={20} />
          {validationErrors.second_phone && <span className="field-error">{validationErrors.second_phone}</span>}
        </div>
        <div className="form-group">
          <label>Address</label>
          <input type="text" className="form-input" value={customerData.address || ''}
            onChange={(e) => updateCustomerField('address', e.target.value)} placeholder="123 Main St"
            disabled={!memberLookupDone} maxLength={200} />
        </div>
        <div className="form-group">
          <label>City</label>
          <input type="text" className="form-input" value={customerData.city || ''}
            onChange={(e) => updateCustomerField('city', e.target.value)}
            disabled={!memberLookupDone} maxLength={50} />
        </div>
        <div className="form-group">
          <label>State</label>
          <input type="text" className="form-input" value={customerData.state || ''}
            onChange={(e) => updateCustomerField('state', e.target.value)}
            disabled={!memberLookupDone} maxLength={20} />
        </div>
        <div className="form-group">
          <label>Zip Code</label>
          <input type="text" className={`form-input${validationErrors.zip_code ? ' input-error' : ''}`}
            value={customerData.zip_code || ''}
            onChange={(e) => updateCustomerField('zip_code', e.target.value)}
            disabled={!memberLookupDone} maxLength={10} />
          {validationErrors.zip_code && <span className="field-error">{validationErrors.zip_code}</span>}
        </div>
        <div className="form-group">
          <label>Country</label>
          <input type="text" className="form-input" value={customerData.country || 'United States'}
            onChange={(e) => updateCustomerField('country', e.target.value)}
            disabled={!memberLookupDone} maxLength={50} />
        </div>
      </div>

      {/* Emergency Contact */}
      <h4 className="wizard-section-title" style={{ marginTop: '1.5rem', fontSize: '0.95rem' }}>Emergency Contact</h4>
      <div className="form-grid">
        <div className="form-group">
          <label>Contact Name</label>
          <input type="text" className="form-input" value={emergencyName}
            onChange={(e) => setEmergencyName(e.target.value)} placeholder="Jane Doe"
            disabled={!memberLookupDone} maxLength={100} />
        </div>
        <div className="form-group">
          <label>Contact Phone</label>
          <input type="tel" className="form-input" value={emergencyPhone}
            onChange={(e) => setEmergencyPhone(formatPhoneInput(e.target.value))} placeholder="(555) 987-6543"
            disabled={!memberLookupDone} maxLength={20} />
        </div>
        <div className="form-group">
          <label>Relationship</label>
          <input type="text" className="form-input" value={emergencyRelation}
            onChange={(e) => setEmergencyRelation(e.target.value)} placeholder="Spouse, Parent, etc."
            disabled={!memberLookupDone} maxLength={50} />
        </div>
      </div>

      {/* Member RV Management */}
      {foundMember && (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 className="wizard-section-title" style={{ fontSize: '0.95rem', margin: 0 }}>
              Recreational Vehicles
            </h4>
            <button
              type="button"
              className="btn btn-primary"
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.6rem' }}
              onClick={() => { setEditingRV(null); setShowRVModal(true); }}
            >
              + Add RV
            </button>
          </div>

          {rvLoading && <div className="loading" style={{ padding: '0.5rem 0' }}>Loading RVs...</div>}

          {!rvLoading && memberRVs.length === 0 && (
            <div style={{ color: 'var(--text-secondary, #888)', fontSize: '0.85rem', padding: '0.5rem 0' }}>
              No recreational vehicles on file.
            </div>
          )}

          {!rvLoading && memberRVs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
              {memberRVs.filter(rv => rv.active).map(rv => (
                <div key={rv.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.5rem 0.75rem', background: 'var(--background, #f9f9f9)',
                  borderRadius: '6px', border: '1px solid var(--border, #ddd)',
                }}>
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong>{rv.type}</strong>
                    {rv.description && <span style={{ marginLeft: '0.5rem', color: 'var(--text-secondary, #888)' }}>— {rv.description}</span>}
                    {rv.length != null && <span style={{ marginLeft: '0.5rem' }}>{rv.length} ft</span>}
                    {rv.licensePlate && <span style={{ marginLeft: '0.5rem' }}>Plate: {rv.licensePlate}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button type="button" className="btn btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                      onClick={() => { setEditingRV(rv); setShowRVModal(true); }}>Edit</button>
                    {rvDeleteConfirm === rv.id ? (
                      <>
                        <button type="button" className="btn btn-danger"
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                          onClick={() => handleDeleteRV(rv.id)}>Confirm</button>
                        <button type="button" className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                          onClick={() => setRvDeleteConfirm(null)}>Cancel</button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-danger"
                        style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                        onClick={() => setRvDeleteConfirm(rv.id)}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Create Member Account */}
      {!foundMember && (
        <div style={{ marginTop: '1.5rem' }}>
          <h4 className="wizard-section-title" style={{ fontSize: '0.95rem', margin: '0 0 0.5rem' }}>
            Step 3: Add This Customer as a New Member
          </h4>
          <label className="radio-option">
            <input type="checkbox" checked={createMemberAccount}
              onChange={(e) => setCreateMemberAccount(e.target.checked)}
              disabled={!memberLookupDone} />
            <span>Create member account — welcome email will be sent to set login credentials</span>
          </label>
        </div>
      )}
      {!foundMember && createMemberAccount && (
        <p style={{ color: 'var(--text-secondary, #888)', fontSize: '0.8rem', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
          RV management will be available after the member account is created.
        </p>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* RV Add/Edit Modal */}
      {showRVModal && foundMember && (
        <RecreationalVehicleFormModal
          isOpen={showRVModal}
          onClose={() => { setShowRVModal(false); setEditingRV(null); }}
          onSaved={() => { setShowRVModal(false); setEditingRV(null); if (foundMember?.id) loadMemberRVs(foundMember.id); }}
          vehicle={editingRV}
          memberId={foundMember.id}
        />
      )}
    </div>
  );

  // ---- Step 4: Fee Review ----

  const canApplyFeeAdjustment =
    feePreview?.admin_capabilities?.can_apply_fee_adjustment ?? adminLevel >= 10;

  const renderFeeReviewStep = () => (
    <div className="wizard-content">
      <h3 className="wizard-section-title">Fee Review</h3>

      {/* Discount code + fee adjustment (recalculate the preview on blur) */}
      <DiscountCodeField
        value={discountCode}
        onChange={setDiscountCode}
        onApply={loadFeePreview}
        error={feePreview?.discount_error?.message}
        disabled={feePreviewLoading}
      />
      <FeeAdjustmentFields
        canApply={canApplyFeeAdjustment}
        value={feeAdjustment}
        onChange={setFeeAdjustment}
        onBlur={loadFeePreview}
        disabled={feePreviewLoading}
      />

      {feePreviewLoading && <div className="loading">Calculating fees...</div>}
      {feePreviewError && <div className="error-message">{feePreviewError}</div>}

      {feePreview && (
        <div className="fee-review-section">
          {/* Per-site breakdown */}
          {feePreview.site_details.map((sd) => (
            <div key={sd.site_alias} className="fee-site-section">
              <div className="fee-site-header">{sd.site_alias}</div>
              <div className="fee-row">
                <span>Base cost</span>
                <span>${(sd.base_cost ?? 0).toFixed(2)}</span>
              </div>
              {(sd.occupancy_fees || []).map((f, i) => (
                <div key={i} className="fee-row">
                  <span>{f.description}</span>
                  <span>${(f.amount ?? 0).toFixed(2)}</span>
                </div>
              ))}
              {(sd.pet_fees || []).map((f, i) => (
                <div key={i} className="fee-row">
                  <span>{f.description}</span>
                  <span>${(f.amount ?? 0).toFixed(2)}</span>
                </div>
              ))}
              {(sd.vehicle_fees || []).map((f, i) => (
                <div key={i} className="fee-row">
                  <span>{f.description}</span>
                  <span>${(f.amount ?? 0).toFixed(2)}</span>
                </div>
              ))}
              {(sd.site_imposed_fees || []).map((f, i) => (
                <div key={i} className="fee-row">
                  <span>{f.name}</span>
                  <span>${(f.amount ?? 0).toFixed(2)}</span>
                </div>
              ))}
              <div className="fee-row site-total">
                <span>Site total</span>
                <span>${(sd.site_total ?? 0).toFixed(2)}</span>
              </div>
            </div>
          ))}

          {/* Booking fees */}
          {(feePreview.booking_fees || []).length > 0 && (
            <>
              <div className="fee-section-divider">Booking Fees</div>
              {feePreview.booking_fees.map((f, i) => (
                <div key={i} className="fee-row">
                  <span>{f.fee_description}</span>
                  <span>${(f.amount ?? 0).toFixed(2)}</span>
                </div>
              ))}
            </>
          )}

          {/* Taxes */}
          {(feePreview.reservation_taxes || []).length > 0 && (
            <>
              <div className="fee-section-divider">Taxes</div>
              {feePreview.reservation_taxes.map((t, i) => (
                <div key={i} className="fee-row">
                  <span>{t.tax_name}{t.description ? ` (${t.description})` : ''}</span>
                  <span>${(t.tax_amount ?? t.amount ?? 0).toFixed(2)}</span>
                </div>
              ))}
            </>
          )}

          {/* Discount + fee adjustment line items */}
          {(() => {
            // The preview endpoint returns fee_collections at the top level; older
            // shapes nested it under fee_breakdown, so fall back to that.
            const fc =
              feePreview.fee_collections ?? feePreview.fee_breakdown?.fee_collections;
            const hasApplied =
              (fc?.discount?.length || 0) > 0 || (fc?.fee_adjustment?.length || 0) > 0;
            return hasApplied ? (
              <>
                <div className="fee-section-divider">Adjustments</div>
                <AppliedFeeLines feeCollections={fc} />
              </>
            ) : null;
          })()}

          {/* Grand total */}
          <div className="fee-grand-total">
            <span>GRAND TOTAL</span>
            <span>${(feePreview.grand_total ?? 0).toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Internal notes (admin-only, create step only) */}
      <div className="admin-fee-field" style={{ marginTop: 16 }}>
        <label>Internal Notes <span className="admin-fee-hint">(not shown to customer)</span></label>
        <textarea
          maxLength={1000}
          rows={2}
          placeholder="General notes about this booking (e.g. Walk-in, paid cash)"
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
        />
      </div>
    </div>
  );

  // ---- Step 5: Payment ----

  const renderPaymentStep = () => (
    <div className="wizard-content">
      <h3 className="wizard-section-title">Payment</h3>

      {/* Payment Method Selection */}
      <div className="payment-methods">
        <label className="payment-method-option">
          <input type="radio" name="paymentMethod" value="cash"
            checked={paymentMethod === 'cash'}
            onChange={() => { setPaymentMethod('cash'); setPaymentStatus('paid'); }} />
          <span>Cash</span>
        </label>
        <label className="payment-method-option">
          <input type="radio" name="paymentMethod" value="check"
            checked={paymentMethod === 'check'}
            onChange={() => { setPaymentMethod('check'); setPaymentStatus('paid'); }} />
          <span>Check</span>
        </label>
        <label className="payment-method-option">
          <input type="radio" name="paymentMethod" value="card_terminal"
            checked={paymentMethod === 'card_terminal'}
            onChange={() => { setPaymentMethod('card_terminal'); setPaymentStatus('paid'); }} />
          <span>Card Terminal</span>
        </label>
        {adminLevel >= 10 && (
          <label className="payment-method-option">
            <input type="radio" name="paymentMethod" value="card_moto"
              checked={paymentMethod === 'card_moto'}
              onChange={() => { setPaymentMethod('card_moto'); setPaymentStatus('paid'); }} />
            <span>Card MOTO</span>
          </label>
        )}
        <label className="payment-method-option">
          <input type="radio" name="paymentMethod" value="payment_link"
            checked={paymentMethod === 'payment_link'}
            onChange={() => { setPaymentMethod('payment_link'); setPaymentStatus('pending'); }} />
          <span>Send Payment Link</span>
        </label>
      </div>

      {/* Method-specific fields */}
      <div className="payment-method-fields">
        {/* Cash / Check: payment status */}
        {(paymentMethod === 'cash' || paymentMethod === 'check') && (
          <div className="form-group">
            <label>Payment Status</label>
            <select className="form-input payment-status-select"
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value as 'paid' | 'pending')}>
              <option value="paid">Paid Now</option>
              <option value="pending">Pay at Check-in</option>
            </select>
          </div>
        )}

        {/* Card Terminal: transaction ID */}
        {paymentMethod === 'card_terminal' && (
          <div className="form-group">
            <label>Transaction ID <span className="required">*</span></label>
            <input type="text" className="form-input" value={terminalTransactionId}
              onChange={(e) => setTerminalTransactionId(e.target.value)}
              placeholder="Enter transaction ID from terminal receipt" />
          </div>
        )}

        {/* Card MOTO */}
        {paymentMethod === 'card_moto' && (
          <>
            {/* Provider info */}
            {storePaymentProviders.length === 0 && (
              <div className="error-message">No payment providers configured for this store.</div>
            )}
            {storePaymentProviders.length === 1 && (
              <p style={{ marginBottom: '0.5rem' }}>
                Provider: <strong>{storePaymentProviders[0].provider.provider_name}</strong>
              </p>
            )}
            {storePaymentProviders.length > 1 && (
              <div className="form-group">
                <label>Payment Provider</label>
                <select className="form-input" defaultValue={storePaymentProviders[0]?.provider.provider_code}>
                  {storePaymentProviders.map(p => (
                    <option key={p.id} value={p.provider.provider_code}>
                      {p.provider.provider_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Card Entry Component */}
            {storePaymentProviders.length > 0 && selectedStoreId && (
              <AdminPaymentCardEntry
                storeId={selectedStoreId}
                amount={feePreview?.grand_total || 0}
                onTokenize={(token) => setCardToken(token)}
                onError={(err) => setError(err)}
                adminLevel={adminLevel}
              />
            )}

            {cardToken && (
              <div style={{ marginTop: '0.5rem', color: '#065f46', fontSize: '0.875rem' }}>
                Card tokenized: {cardToken.card_brand} ending in {cardToken.last_four_digits}
              </div>
            )}
          </>
        )}

        {/* Payment Link */}
        {paymentMethod === 'payment_link' && (
          <div className="info-box">
            <p>A payment link will be emailed to <strong>{customerData.email}</strong>.</p>
            <p>The reservation will be held while awaiting payment. If the customer does not pay before the link expires, the reservation is released automatically.</p>
          </div>
        )}
      </div>

      {/* Email Toggle */}
      <div className="payment-extras">
        {/* Email Recipient Confirmation */}
        <div style={{
          marginTop: '0.75rem',
          padding: '0.75rem',
          backgroundColor: emailAddressConfirmed ? '#ecfdf5' : '#f0f9ff',
          border: `1px solid ${emailAddressConfirmed ? '#a7f3d0' : '#bae6fd'}`,
          borderRadius: '6px'
        }}>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>Email Recipient</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Name: <strong>{customerData.first_name} {customerData.last_name}</strong>
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Email: <strong>{customerData.email}</strong>
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={emailAddressConfirmed}
              onChange={(e) => setEmailAddressConfirmed(e.target.checked)} />
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>I confirm this is the correct recipient</span>
          </label>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  // ---- Step 6: Confirmation ----

  const renderConfirmationStep = () => {
    if (!bookingResult) {
      return <div className="wizard-content"><div className="loading">Submitting booking...</div></div>;
    }

    // Branch 1: Success (confirmed + paid)
    if (bookingResult.success && bookingResult.status === 'confirmed') {
      return (
        <div className="wizard-content result-content confirmation-success">
          <div className="result-icon success">{'\u2713'}</div>
          <h3 className="result-title">Reservation Created!</h3>
          <div className="result-details">
            <p className="confirmation-code">Confirmation: <strong>{bookingResult.confirmation_code}</strong></p>
            <p>Reservation ID: {bookingResult.reservation_id}</p>
            <p>Total: ${bookingResult.cost?.toFixed(2)}</p>
            <p>Payment: {bookingResult.payment_method} - {bookingResult.payment_status}</p>
            {bookingResult.payment_id && <p>Payment ID: {bookingResult.payment_id}</p>}
          </div>
        </div>
      );
    }

    // Branch 2: Payment Link Sent
    if (bookingResult.success && bookingResult.payment_method === 'payment_link' && !bookingResult.payment_error) {
      return (
        <div className="wizard-content result-content confirmation-success">
          <div className="result-icon success">{'✓'}</div>
          <h3 className="result-title">Payment Link Sent!</h3>
          <div className="result-details">
            <p className="confirmation-code">Confirmation: <strong>{bookingResult.confirmation_code}</strong></p>
            <p>Reservation ID: {bookingResult.reservation_id}</p>
            <p>Total: ${bookingResult.cost?.toFixed(2)}</p>
            <p>
              A payment link has been emailed to <strong>{customerData.email}</strong>.{' '}
              {bookingResult.expires_at
                ? <>The reservation is held until <strong>{parseBackendUtc(bookingResult.expires_at).toLocaleString()}</strong> — after that, the site is released and the reservation is cancelled.</>
                : 'The reservation is held pending payment and is released automatically if the link expires unpaid.'
              }
            </p>
          </div>
        </div>
      );
    }

    // Branch 3: Pending Payment (MOTO failed)
    if (bookingResult.success && (bookingResult.status === 'pending_payment' || bookingResult.payment_error)) {
      return (
        <div className="wizard-content result-content confirmation-pending">
          <div className="result-icon pending">{'\u26A0'}</div>
          <h3 className="result-title">Reservation Created - Payment Failed</h3>
          <div className="result-details">
            <p className="confirmation-code">Confirmation: <strong>{bookingResult.confirmation_code}</strong></p>
            <p>Reservation ID: {bookingResult.reservation_id}</p>
            {bookingResult.payment_error && (
              <p className="error-message">{bookingResult.payment_error}</p>
            )}
            {bookingResult.expires_at && (
              <p className={`expiry-timer ${timeRemaining === 'EXPIRED' ? 'expired' : ''}`}>
                Hold expires in: {timeRemaining || 'calculating...'}
              </p>
            )}
          </div>

          {/* Retry Payment */}
          <div className="retry-payment-section">
            <h4>Retry Payment</h4>
            {timeRemaining === 'EXPIRED' ? (
              // Once the hold lapses the backend rejects every process-payment
              // call, so don't let the manager collect card details for a charge
              // that cannot land. Rebooking as a payment link is the one action
              // still available — and it only becomes possible now, because the
              // lapsed hold has released the site.
              <div className="retry-error">
                The 10-minute hold on this reservation has expired and the site has
                been released. This booking can no longer be paid — send the customer
                a payment link below, or close this window and start again.
              </div>
            ) : (
            <>
            <div className="retry-payment-methods">
              <label className="radio-option">
                <input type="radio" name="retryMethod" value="card_moto"
                  checked={retryPaymentMethod === 'card_moto'}
                  onChange={() => setRetryPaymentMethod('card_moto')} />
                <span>Retry with Different Card</span>
              </label>
              <label className="radio-option">
                <input type="radio" name="retryMethod" value="cash"
                  checked={retryPaymentMethod === 'cash'}
                  onChange={() => setRetryPaymentMethod('cash')} />
                <span>Pay with Cash</span>
              </label>
              <label className="radio-option">
                <input type="radio" name="retryMethod" value="check"
                  checked={retryPaymentMethod === 'check'}
                  onChange={() => setRetryPaymentMethod('check')} />
                <span>Pay with Check</span>
              </label>
              <label className="radio-option">
                <input type="radio" name="retryMethod" value="card_terminal"
                  checked={retryPaymentMethod === 'card_terminal'}
                  onChange={() => setRetryPaymentMethod('card_terminal')} />
                <span>Pay with Card Terminal</span>
              </label>
            </div>

            {retryPaymentMethod === 'card_moto' && selectedStoreId && (
              <>
                <AdminPaymentCardEntry
                  storeId={selectedStoreId}
                  amount={bookingResult.cost || feePreview?.grand_total || 0}
                  onTokenize={(token) => setRetryCardToken(token)}
                  onError={(err) => setRetryError(err)}
                  adminLevel={adminLevel}
                  nextActionLabel={'Click "Charge Card" to process the payment.'}
                  resetSignal={retryCardResetSignal}
                />
                {retryCardToken && (
                  <button className="btn btn-primary" style={{ marginTop: '0.5rem' }}
                    onClick={() => handleRetryPayment('card_moto', retryCardToken)}
                    disabled={retryLoading}>
                    {retryLoading ? 'Charging...' : 'Charge Card'}
                  </button>
                )}
              </>
            )}

            {(retryPaymentMethod === 'cash' || retryPaymentMethod === 'check') && (
              <button className="btn btn-primary" style={{ marginTop: '0.5rem' }}
                onClick={() => handleRetryPayment(retryPaymentMethod!)}
                disabled={retryLoading}>
                {retryLoading ? 'Processing...' : `Confirm ${retryPaymentMethod === 'cash' ? 'Cash' : 'Check'} Payment`}
              </button>
            )}

            {retryPaymentMethod === 'card_terminal' && (
              <>
                <div className="form-group" style={{ marginTop: '0.5rem' }}>
                  <label>Transaction ID <span className="required">*</span></label>
                  <input type="text" className="form-input" value={retryTerminalTransactionId}
                    onChange={(e) => setRetryTerminalTransactionId(e.target.value)}
                    placeholder="Enter transaction ID from terminal receipt" />
                </div>
                <button className="btn btn-primary" style={{ marginTop: '0.5rem' }}
                  onClick={() => handleRetryPayment('card_terminal')}
                  disabled={retryLoading || !retryTerminalTransactionId.trim()}>
                  {retryLoading ? 'Processing...' : 'Confirm Terminal Payment'}
                </button>
              </>
            )}
            </>
            )}

            {/* Payment-link fallback — shown in both states, but only actionable
                once the failed hold has released the site. Skipped when this
                booking is already a payment link (the email just failed to send,
                so rebooking would duplicate it) or when there is no hold to time,
                which would leave a button that never enables. */}
            {bookingResult.payment_method !== 'payment_link' && bookingResult.expires_at && (
            <div className="payment-link-fallback" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 0.25rem' }}>Send Payment Link Instead</h4>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.5rem' }}>
                Rebooks the same dates, sites and guest details and emails{' '}
                <strong>{customerData.email}</strong> a link to pay. This creates a new
                reservation with a new confirmation code.
              </p>
              {timeRemaining !== 'EXPIRED' && (
                <p style={{ fontSize: '0.875rem', color: '#92400e', margin: '0 0 0.5rem' }}>
                  Available in <strong>{timeRemaining || '...'}</strong> — the failed
                  attempt is still holding {selectedSites.length === 1 ? 'this site' : 'these sites'}.
                  Retry the payment above, or wait for the hold to lapse.
                </p>
              )}
              <button
                className="btn btn-primary"
                onClick={handleSendPaymentLinkInstead}
                disabled={timeRemaining !== 'EXPIRED' || linkFallbackLoading}
              >
                {linkFallbackLoading ? 'Sending...' : 'Send Payment Link'}
              </button>
            </div>
            )}

            {retryError && <div className="retry-error">{retryError}</div>}
          </div>
        </div>
      );
    }

    // Branch 3: Failed
    return (
      <div className="wizard-content result-content confirmation-error">
        <div className="result-icon error">{'\u2717'}</div>
        <h3 className="result-title">Booking Failed</h3>
        <div className="result-details">
          <p className="error-message">{bookingResult.error || bookingResult.message || 'An unknown error occurred'}</p>
        </div>
        <button className="btn btn-secondary" onClick={() => setCurrentStep('payment')}
          style={{ marginTop: '1rem' }}>
          Go Back to Payment
        </button>
      </div>
    );
  };

  // ---- Render ----

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'store_dates': return renderStoreDatesStep();
      case 'sites': return renderSitesStep();
      case 'customer': return renderCustomerStep();
      case 'fee_review': return renderFeeReviewStep();
      case 'payment': return renderPaymentStep();
      case 'confirmation': return renderConfirmationStep();
      default: return null;
    }
  };

  const isConfirmation = currentStep === 'confirmation';
  const isConfirmedSuccess = isConfirmation && bookingResult?.success && bookingResult?.status === 'confirmed';

  const footer = isConfirmation ? (
    <>
      {isConfirmedSuccess && (
        <>
          <button className="btn btn-secondary" onClick={handleClose}>
            Done
          </button>
          <button className="btn btn-primary" onClick={resetWizard}>
            Create Another Booking
          </button>
        </>
      )}
      {!isConfirmedSuccess && !bookingResult?.success && (
        <button className="btn btn-secondary" onClick={handleClose}>
          Close
        </button>
      )}
      {bookingResult?.success && bookingResult?.status === 'pending_payment' && (
        <button className="btn btn-secondary" onClick={handleClose}>
          Close
        </button>
      )}
    </>
  ) : (
    <>
      <button className="btn btn-secondary" onClick={handleClose} disabled={loading}>
        CANCEL
      </button>
      {currentStepIndex > 0 && (
        <button className="btn btn-secondary" onClick={handleBack} disabled={loading}>
          {'\u2190'} BACK
        </button>
      )}
      {currentStep === 'payment' ? (
        <button className="btn btn-primary" onClick={handleNext} disabled={!canProceed() || loading}>
          {loading ? 'SUBMITTING...' : 'SUBMIT BOOKING'}
        </button>
      ) : (
        <button className="btn btn-primary" onClick={handleNext} disabled={!canProceed() || loading}>
          {loading ? 'LOADING...' : 'NEXT \u2192'}
        </button>
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Admin Create Reservation - Step ${currentStepIndex + 1}/${STEPS.length}`}
      size="large"
      footer={footer}
      preventBackdropClose
    >
      <div className="create-wizard" ref={modalBodyRef}>
        {!isConfirmation && renderStepIndicator()}
        {renderCurrentStep()}
      </div>
    </Modal>
  );
};

export default AdminCreateReservationWizard;
